import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { BeautifulMentionsPlugin, BeautifulMentionNode } from 'lexical-beautiful-mentions';

// added ListPlugin + nodes for list support:
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
// eslint-disable-next-line import/no-extraneous-dependencies
import { ListNode, ListItemNode } from '@lexical/list';

import { QuestionColumnMentionNode, LexicalContentUpdaterPlugin } from '../../../../../components';

const StyledContentEditable = styled(ContentEditable)`
  color: var(--content-body);

  &:focus {
    outline: none;
  }

  ul {
    padding-left: 10px;
    margin: 0;
  }

  ul li {
    position: relative;
    margin: 0;
    font-family: var(--font-family-body);
    font-size: 13px;
    line-height: 24px;
    font-style: normal;
    letter-spacing: -0.02em;
    font-weight: 400;
  }

  .lexical-readonly__paragraph {
    margin: 0;
    font-family: var(--font-family-body);
    color: var(--content-body);
    font-size: 13px;
    line-height: 24px;
    font-style: normal;
    letter-spacing: -0.02em;
    font-weight: 400;
    white-space: normal;
    word-break: break-word;
  }
`;

const PUNCTUATION = '\\,\\*\\?\\$\\|#{}\\(\\)\\^\\[\\]\\\\/!%\'"~=<>:;';

/**
 * Component: ReadOnlyWithLexicalTags
 *
 * Renders a **read-only** Lexical editor with support for mentions and rich text formatting.
 *
 * @param {Object} props - Component props
 * @param {string} props.text - The text content to display within the editor.
 */
const ReadOnlyWithLexicalTags = ({ text, namespace }) => {
  const initialConfig = {
    namespace,
    // eslint-disable-next-line no-console
    onError: (error) => console.error('Lexical Error:', error),
    editable: false,
    theme: {
      paragraph: 'lexical-readonly__paragraph',
      beautifulMentions: {
        '@': {
          trigger: 'mentionTrigger',
          value: 'mentionValue',
          container: 'mentionContainer',
          containerFocused: 'mentionContainerFocused'
        },
        '#': {
          trigger: 'mentionTrigger',
          value: 'mentionValue',
          container: 'mentionContainer',
          containerFocused: 'mentionContainerFocused'
        }
      }
    },
    nodes: [
      // register list nodes so Markdown can create actual lists
      ListNode,
      ListItemNode,
      QuestionColumnMentionNode,
      {
        replace: BeautifulMentionNode,
        with: (node) =>
          new QuestionColumnMentionNode(node.getTrigger(), node.getValue(), node.getData())
      }
    ]
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <LexicalContentUpdaterPlugin text={text} PUNCTUATION={PUNCTUATION} />
      <RichTextPlugin
        contentEditable={<StyledContentEditable />}
        ErrorBoundary={LexicalErrorBoundary}
      />
      {/* this plugin actually renders <ListNode> / <ListItemNode> */}
      <ListPlugin />
      <BeautifulMentionsPlugin menuAnchorClassName='hidden' punctuation={PUNCTUATION} />
    </LexicalComposer>
  );
};

ReadOnlyWithLexicalTags.propTypes = {
  text: PropTypes.string.isRequired,
  namespace: PropTypes.string.isRequired
};

export { ReadOnlyWithLexicalTags };
